import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BirthInput } from './astro/chart';
import { dayKey, Reading } from './draw';
import { JournalEntry } from './journal';

const STORAGE_KEY = 'arcana:v1';

/** The history keeps the most recent readings; older ones fall off so storage stays small. */
export const HISTORY_LIMIT = 200;

/** A birth chart the person asked for. Only the birth data is kept; the chart is recomputed on opening. */
export interface SavedChart {
    id: string;
    /** Milliseconds since the epoch. */
    at: number;
    /** Whose chart it is; empty for the person's own. */
    name: string;
    input: BirthInput;
}

/** Birth charts kept on the device, newest first. */
export const CHARTS_LIMIT = 20;

/** Who the card of the day is addressed to. Asked once; `asked` stays true when the person skips it. */
export interface Profile {
    name: string;
    asked: boolean;
}

export interface TarotState {
    profile: Profile;
    /** Today's card. It is kept (and viewable) until local midnight, then ignored. */
    daily: { day: string; reading: Reading } | null;
    /** Newest first. Only readings whose video was watched (unlocked) are kept. */
    history: Reading[];
    muted: boolean;
    /** Whether the person asked for the morning and midnight reminders (and allowed notifications). */
    reminders: boolean;
    /** The first-visit offer of reminders was shown (and answered either way); it is not repeated. */
    remindersAsked: boolean;
    charts: SavedChart[];
    /** Gratitude journal, newest day first, one entry per local day. */
    journal: JournalEntry[];
}

export const defaultState = (): TarotState => ({
    profile: { name: '', asked: false },
    daily: null,
    history: [],
    muted: false,
    reminders: false,
    remindersAsked: false,
    charts: [],
    journal: [],
});

export const loadTarotState = (): TarotState => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultState();
        const parsed = JSON.parse(raw) as Partial<TarotState>;
        return {
            ...defaultState(),
            ...parsed,
            profile: {
                ...defaultState().profile,
                ...(parsed.profile ?? {}),
            },
            history: Array.isArray(parsed.history) ? parsed.history : [],
            charts: Array.isArray(parsed.charts) ? parsed.charts : [],
            journal: Array.isArray(parsed.journal) ? parsed.journal : [],
        };
    } catch {
        return defaultState();
    }
};

export const saveTarotState = (state: TarotState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Storage full or blocked: the app still works for this session.
    }
};

const tarotSlice = createSlice({
    name: 'tarot',
    initialState: loadTarotState,
    reducers: {
        /**
         * Today's card is drawn the moment the person asks for it, before the video, so closing the app
         * does not buy a re-draw. It stays sealed (`unlocked: false`) until `commitReading`.
         */
        sealDaily(state, action: PayloadAction<Reading>) {
            state.daily = {
                day: dayKey(new Date(action.payload.at)),
                reading: { ...action.payload, unlocked: false },
            };
        },
        /** The video was watched: the reading is revealed and saved to the history. */
        commitReading(state, action: PayloadAction<Reading>) {
            const reading = { ...action.payload, unlocked: true };
            if (state.daily?.reading.id === reading.id)
                state.daily.reading = reading;
            state.history = [
                reading,
                ...state.history.filter((r) => r.id !== reading.id),
            ].slice(0, HISTORY_LIMIT);
        },
        deleteReading(state, action: PayloadAction<string>) {
            state.history = state.history.filter(
                (r) => r.id !== action.payload
            );
        },
        clearHistory(state) {
            state.history = [];
        },
        /** The ad was watched: the birth chart is shown and its birth data kept. */
        saveChart(state, action: PayloadAction<SavedChart>) {
            state.charts = [
                action.payload,
                ...state.charts.filter((c) => c.id !== action.payload.id),
            ].slice(0, CHARTS_LIMIT);
        },
        deleteChart(state, action: PayloadAction<string>) {
            state.charts = state.charts.filter((c) => c.id !== action.payload);
        },
        saveJournalEntry(state, action: PayloadAction<JournalEntry>) {
            state.journal = [
                action.payload,
                ...state.journal.filter((e) => e.day !== action.payload.day),
            ].sort((a, b) => (a.day < b.day ? 1 : -1));
        },
        clearJournal(state) {
            state.journal = [];
        },
        setMuted(state, action: PayloadAction<boolean>) {
            state.muted = action.payload;
        },
        setReminders(state, action: PayloadAction<boolean>) {
            state.reminders = action.payload;
            state.remindersAsked = true;
        },
        dismissReminderOffer(state) {
            state.remindersAsked = true;
        },
        /** The name for the card of the day (empty when skipped). Either way the person is not asked again. */
        setProfileName(state, action: PayloadAction<string>) {
            state.profile = { name: action.payload.trim(), asked: true };
        },
    },
});

export const {
    setProfileName,
    sealDaily,
    commitReading,
    deleteReading,
    clearHistory,
    saveChart,
    deleteChart,
    saveJournalEntry,
    clearJournal,
    setMuted,
    setReminders,
    dismissReminderOffer,
} = tarotSlice.actions;

/** Today's card, or null if none was drawn since the last local midnight. */
export const dailyFor = (
    state: TarotState,
    now: Date = new Date()
): Reading | null =>
    state.daily && state.daily.day === dayKey(now) ? state.daily.reading : null;

export default tarotSlice;
